/**
 * Your own vehicle on the map, seen from above with the front pointing up (24 × 44, turned by the map with your
 * heading). Each person picks theirs; it's saved with their account.
 */
import { useId, type CSSProperties } from 'react';

export const CAR_CHOICES: { id: string; label: string }[] = [
  { id: 'patrol', label: 'Patrol car' },
  { id: 'grey-suv-3', label: 'Grey SUV, No. 3 on the roof' },
  { id: 'black-suv-rooster', label: 'Black SUV, Rhode Island Red rooster' },
  { id: 'camaro-69-dog', label: 'White ’69 Camaro, her face on the roof' },
];

export function CarShape({ id, style }: { id: string | null | undefined; style?: CSSProperties }) {
  const clip = useId();
  if (id === 'camaro-69-dog') {
    // A '69 muscle car from above: long hood, short deck, twin black stripes, black vinyl top with her face on it.
    return (
      <svg viewBox="0 0 24 44" width="24" height="44" aria-hidden="true" style={style}>
        <defs>
          <clipPath id={clip}>
            <circle cx="12" cy="24" r="6" />
          </clipPath>
        </defs>
        <rect x="2.2" y="1.2" width="19.6" height="41.6" rx="5" fill="#f8fafc" stroke="#475569" strokeWidth="1" />
        <path d="M2.6 6.5 Q1.6 8 2.2 11M21.4 6.5 Q22.4 8 21.8 11M2.6 31 Q1.6 33 2.2 36M21.4 31 Q22.4 33 21.8 36" stroke="#94a3b8" strokeWidth="0.8" fill="none" />
        <rect x="8.8" y="1.6" width="2" height="13" fill="#111827" />
        <rect x="13.2" y="1.6" width="2" height="13" fill="#111827" />
        <rect x="8.8" y="34.2" width="2" height="8.2" fill="#111827" />
        <rect x="13.2" y="34.2" width="2" height="8.2" fill="#111827" />
        <path d="M4.6 14.4 Q12 12.4 19.4 14.4 L18.4 17.8 Q12 16.8 5.6 17.8 Z" fill="#1e293b" />
        <rect x="5.4" y="17.6" width="13.2" height="13" rx="2.4" fill="#111827" />
        <circle cx="12" cy="24" r="6.4" fill="#f8fafc" />
        <image href="/car-dog-face.png" x="6" y="18" width="12" height="12" clipPath={`url(#${clip})`} preserveAspectRatio="xMidYMid slice" />
        <path d="M5.8 30.6 Q12 31.8 18.2 30.6 L18.8 33.6 Q12 34.6 5.2 33.6 Z" fill="#1e293b" />
        <rect x="3" y="0.4" width="18" height="1.3" rx="0.6" fill="#d1d5db" />
        <rect x="3" y="42.3" width="18" height="1.3" rx="0.6" fill="#d1d5db" />
        <circle cx="5.2" cy="2.9" r="1.1" fill="#fde68a" />
        <circle cx="18.8" cy="2.9" r="1.1" fill="#fde68a" />
        <rect x="3.6" y="41.2" width="4" height="1" rx="0.5" fill="#dc2626" />
        <rect x="16.4" y="41.2" width="4" height="1" rx="0.5" fill="#dc2626" />
      </svg>
    );
  }
  if (id === 'black-suv-rooster') {
    // A black mid-size SUV with a Rhode Island Red rooster on the roof: mahogany body, dark green-black tail.
    return (
      <svg viewBox="0 0 24 44" width="24" height="44" aria-hidden="true" style={style}>
        <rect x="1.8" y="1" width="20.4" height="42" rx="6" fill="#0b0f17" stroke="#fff" strokeWidth="1.3" />
        <rect x="3.2" y="2.2" width="17.6" height="9.4" rx="4" fill="#1c222d" />
        <path d="M7 3.6 Q12 2.6 17 3.6" stroke="#2c3442" strokeWidth="0.7" fill="none" />
        <path d="M4 11.6 Q12 9.6 20 11.6 L19 15.6 Q12 14.4 5 15.6 Z" fill="#3b4a5e" />
        <rect x="4.8" y="15.8" width="14.4" height="19.6" rx="2.2" fill="#161b24" />
        <path d="M4.4 16.4v18.6M19.6 16.4v18.6" stroke="#4b5563" strokeWidth="0.8" strokeLinecap="round" />
        <g transform="translate(3.4 17) scale(0.84)">
          <path d="M13 10.2 C14 5.5 17.5 3.4 19.4 5.6 C17.8 5.6 16.8 6.6 16.4 8.2 C17.8 7 19.8 7.8 19.6 10.2 C18.2 9.6 16.8 10.2 16 11.4 Z" fill="#1c3326" stroke="#e5e7eb" strokeWidth="0.35" />
          <path d="M6.4 7.4 C5.2 9.6 5.2 12.6 7.6 14.6 C10 16.4 13.6 16 15.6 13.4 C16.6 12 16.2 10.4 14.6 10 C12.2 10.6 9.6 10.2 8.2 7.2 Z" fill="#9a3412" stroke="#e5e7eb" strokeWidth="0.35" />
          <path d="M7.4 10.6 C9.4 12.6 12.2 12.8 14.6 11.4" stroke="#c2410c" strokeWidth="1.1" fill="none" strokeLinecap="round" />
          <circle cx="6.9" cy="6.3" r="1.9" fill="#b45309" stroke="#e5e7eb" strokeWidth="0.35" />
          <path d="M5.6 4.8 Q5.6 2.8 6.6 3.6 Q7.2 2.2 8 3.6 Q9 3.2 8.6 5 Z" fill="#dc2626" />
          <path d="M5.1 6 L3.2 6.6 L5.1 7.1 Z" fill="#f59e0b" />
          <ellipse cx="5.8" cy="8.1" rx="0.6" ry="0.9" fill="#dc2626" />
          <circle cx="6.6" cy="5.9" r="0.4" fill="#fde68a" />
          <path d="M9.8 15.4 L9.4 18.4 M12 15.6 L12.4 18.4 M8.6 18.4h1.6 M11.6 18.4h1.6" stroke="#f59e0b" strokeWidth="0.7" strokeLinecap="round" />
        </g>
        <path d="M5 37.8 Q12 38.8 19 37.8 L19.4 40.8 Q12 41.9 4.6 40.8 Z" fill="#3b4a5e" />
        <rect x="0.2" y="12.2" width="2" height="2.6" rx="0.8" fill="#111827" stroke="#fff" strokeWidth="0.3" />
        <rect x="21.8" y="12.2" width="2" height="2.6" rx="0.8" fill="#111827" stroke="#fff" strokeWidth="0.3" />
        <rect x="3.6" y="1.7" width="4.4" height="1.4" rx="0.7" fill="#e5e7eb" />
        <rect x="16" y="1.7" width="4.4" height="1.4" rx="0.7" fill="#e5e7eb" />
        <rect x="3.4" y="41.6" width="17.2" height="0.9" rx="0.45" fill="#dc2626" />
      </svg>
    );
  }
  if (id === 'grey-suv-3') {
    // A full-size grey SUV: square body, long roof with rails, a bold 3 on the roof.
    return (
      <svg viewBox="0 0 24 44" width="24" height="44" aria-hidden="true" style={style}>
        <rect x="1.5" y="1" width="21" height="42" rx="4.5" fill="#6b7280" stroke="#fff" strokeWidth="1.3" />
        {/* hood */}
        <rect x="3" y="2.2" width="18" height="8.8" rx="3" fill="#7c8490" />
        <path d="M6 4.5h12" stroke="#5b626c" strokeWidth="0.6" />
        {/* windshield */}
        <path d="M4 11.2 Q12 9.4 20 11.2 L19 15.2 Q12 14 5 15.2 Z" fill="#1f2937" />
        {/* roof */}
        <rect x="4.6" y="15.4" width="14.8" height="21.6" rx="1.6" fill="#9ca3af" />
        <path d="M4.2 16v20.6M19.8 16v20.6" stroke="#374151" strokeWidth="0.9" strokeLinecap="round" />
        <text x="12" y="31.6" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontWeight="900" fontSize="14" fill="#111827" stroke="#fff" strokeWidth="0.7" paintOrder="stroke">
          3
        </text>
        {/* rear glass */}
        <path d="M5 37.4 Q12 38.4 19 37.4 L19.4 40.6 Q12 41.8 4.6 40.6 Z" fill="#1f2937" />
        {/* mirrors and lights */}
        <rect x="0" y="12" width="2" height="2.6" rx="0.8" fill="#4b5563" />
        <rect x="22" y="12" width="2" height="2.6" rx="0.8" fill="#4b5563" />
        <rect x="3.4" y="1.6" width="4.2" height="1.6" rx="0.8" fill="#fde68a" />
        <rect x="16.4" y="1.6" width="4.2" height="1.6" rx="0.8" fill="#fde68a" />
        <rect x="3" y="41.4" width="3.6" height="1.2" rx="0.6" fill="#dc2626" />
        <rect x="17.4" y="41.4" width="3.6" height="1.2" rx="0.6" fill="#dc2626" />
      </svg>
    );
  }
  // A patrol car: black hood and trunk, white doors, red and blue light bar.
  return (
    <svg viewBox="0 0 24 44" width="24" height="44" aria-hidden="true" style={style}>
      <rect x="2" y="1.5" width="20" height="41" rx="7" fill="#111827" stroke="#fff" strokeWidth="1.5" />
      <rect x="3.2" y="13" width="17.6" height="18" fill="#fff" />
      <path d="M5 11.5 Q12 8.5 19 11.5 L18 16 Q12 14.5 6 16 Z" fill="#7dd3fc" />
      <path d="M6 30 Q12 31.5 18 30 L18.6 34 Q12 36 5.4 34 Z" fill="#7dd3fc" />
      <rect x="5" y="20" width="7" height="3.6" rx="1" fill="#dc2626" />
      <rect x="12" y="20" width="7" height="3.6" rx="1" fill="#2563eb" />
      <rect x="4" y="2.5" width="4" height="2" rx="1" fill="#fde68a" />
      <rect x="16" y="2.5" width="4" height="2" rx="1" fill="#fde68a" />
    </svg>
  );
}
